/**
 * Landing v3 — where the one floating Ka sits once the page scrolls on below
 * the features, on both layouts (desktop's `KaTravel`, the phone's `PhoneKa`).
 *
 * The blocks he speaks in — the agent chat, the map, the posts — each hold an
 * empty `[data-lv3-ka]` slot above their headline (the close's is the desk
 * Ka's head). Ka RIDES a block's slot while it is in the reading band — the
 * part of the screen between the top chrome and the bottom one — locked to it
 * as the page scrolls. Once the reader is past the headline (the slot has left
 * the band's top) Ka floats on to the band's lower edge and waits there for
 * the next block, whose slot rises to meet him; he then rides that one — unless
 * a long read lies between the two (the tiles, after the chat: `parkGap`).
 * Then he stays on his slot until it has scrolled off the screen (user
 * 2026-09-30: "ka only moves when its place gets out of the screen") and goes
 * back to his corner until the next slot reaches the band, as before the
 * first. Scrolling back up mirrors it all.
 */

/** A slot's box on screen (its `getBoundingClientRect`). */
export type KaSlotRect = { left: number; top: number; width: number };

/** The reading band: its top edge, and how far above the screen's bottom a
 *  slot's BOTTOM edge may sit (so the band's lower edge depends on the slot's
 *  size), for a screen `height` tall. */
export type KaBand = { top: number; bottomInset: number; height: number };

/** What the last call decided, handed back to the next one. */
export type KaTravelState = { reached: number; floating: boolean };

export const KA_TRAVEL_START: KaTravelState = { reached: -1, floating: false };

/** A slot already reached (or already floated past) holds on for this many px
 *  more, so a scroll resting on an edge cannot flip Ka back and forth. */
export const KA_BAND_HYSTERESIS = 24;

export type KaSlotPick = KaTravelState & {
  /** The slot Ka is on (riding it or waiting for it); -1: his corner. */
  slot: number;
  /** The LAST slot is reached: the desk Ka stands in there. */
  final: boolean;
  /** Where Ka's card goes on screen (top-left) and its size. */
  x: number;
  y: number;
  size: number;
};

export function kaSlotAt(
  rects: readonly KaSlotRect[],
  band: KaBand,
  prev: KaTravelState,
  {
    parkGap = Infinity,
  }: {
    /** Slots further apart than this (px, top to top) have a long read
     *  between them: past the first, Ka waits in his corner, not mid-screen. */
    parkGap?: number;
  } = {},
): KaSlotPick {
  const bandBottom = (size: number) => band.height - band.bottomInset - size;
  // The last slot whose top has reached the band.
  let reached = -1;
  rects.forEach((r, i) => {
    const slack = i <= prev.reached ? KA_BAND_HYSTERESIS : 0;
    if (r.top <= bandBottom(r.width) + slack) reached = i;
  });
  const final = reached >= 0 && reached === rects.length - 1;
  if (reached < 0) {
    return { reached, floating: false, slot: -1, final, x: 0, y: 0, size: 0 };
  }
  // Floated on once the slot is past the band's top by the slack, back only
  // when it is fully back in. The last slot has no next: Ka stays on it.
  const past = rects[reached].top < band.top - (prev.floating ? 0 : KA_BAND_HYSTERESIS);
  const slot = past && reached + 1 < rects.length ? reached + 1 : reached;
  if (slot !== reached && rects[slot].top - rects[reached].top > parkGap) {
    // A long read follows: Ka keeps riding his slot until it is off the top
    // of the screen, then parks (back on it once the slack of it shows).
    const r = rects[reached];
    const gone = r.top + r.width < (prev.floating ? KA_BAND_HYSTERESIS : 0);
    if (gone) return { reached, floating: true, slot: -1, final, x: 0, y: 0, size: 0 };
    return {
      reached,
      floating: false,
      slot: reached,
      final,
      x: r.left,
      y: r.top,
      size: r.width,
    };
  }
  const r = rects[slot];
  // Riding: follow the slot, held at the band's lower edge while it is still
  // in the slack below it. Waiting: the next slot's spot, kept in the band —
  // the same slot, so the moment it arrives Ka is already on it.
  const y =
    slot === reached
      ? Math.min(r.top, bandBottom(r.width))
      : Math.min(Math.max(r.top, band.top), bandBottom(r.width));
  return {
    reached,
    floating: slot !== reached,
    slot,
    final,
    x: r.left,
    y,
    size: r.width,
  };
}
